import { FeatureFlags } from './FeatureFlags.js';
import { EventBus } from '../engine/EventBus.js';
import { Clock } from '../engine/Clock.js';
import { LegacyV31Adapter } from '../compat/LegacyV31Adapter.js';
import { installTestApi } from '../devtools/TestApi.js';
import { TestLab } from '../devtools/TestLab.js';

export class GameApp {
  constructor({ mount }) {
    this.mount = mount;
    this.bus = new EventBus();
    this.clock = new Clock();
    this.features = new FeatureFlags();
    this.runtimeState = {
      activeScenario: null,
      timeOfDay: 'day',
      weather: 'clear',
      simulationSteps: 0,
    };
    this.lastFrame = 0;
  }

  start() {
    this.#renderShell();
    this.#wireEvents();
    this.testApi = installTestApi({
      bus: this.bus,
      clock: this.clock,
      featureFlags: this.features,
      getSnapshot: () => ({ ...this.runtimeState }),
    });
    this.testLab = new TestLab({ bus: this.bus, testApi: this.testApi });
    requestAnimationFrame((timestamp) => this.#frame(timestamp));
  }

  #renderShell() {
    this.mount.innerHTML = `
      <main class="shell">
        <header class="topbar">
          <div>
            <p class="eyebrow">SERPENT: BORN WILD</p>
            <h1>Engine 2.0 Development Platform</h1>
          </div>
          <div class="build-badge">${__BUILD_CHANNEL__}</div>
        </header>
        <section class="hero">
          <div>
            <h2>v32 compatibility shell is running.</h2>
            <p>The existing v31 game remains isolated while systems are extracted into deterministic modules.</p>
            <div class="actions">
              <button type="button" data-action="legacy">Launch v31 Baseline</button>
              <button type="button" data-action="test-lab">Open Test Lab</button>
            </div>
          </div>
          <dl class="status-grid">
            <div><dt>Engine 2.0</dt><dd>Enabled</dd></div>
            <div><dt>Simulation</dt><dd data-status="simulation">Running</dd></div>
            <div><dt>Scenario</dt><dd data-status="scenario">None</dd></div>
            <div><dt>Commit</dt><dd>${__BUILD_COMMIT__.slice(0, 8)}</dd></div>
          </dl>
        </section>
        <section class="legacy-host" data-legacy-host>
          <div class="legacy-placeholder">
            <strong>v31 compatibility baseline</strong>
            <span>Import the legacy build with scripts/IMPORT_V31.ps1.</span>
          </div>
        </section>
        <nav class="play-controls" data-play-controls hidden aria-label="Game development controls">
          <button type="button" class="play-controls__tab" data-action="controls-toggle" aria-expanded="false">DEV</button>
          <div class="play-controls__menu" data-controls-menu hidden>
            <button type="button" data-action="exit-game">Exit Game</button>
            <button type="button" data-action="test-lab">Test Lab</button>
          </div>
        </nav>
      </main>
    `;

    const host = this.mount.querySelector('[data-legacy-host]');
    this.shell = this.mount.querySelector('.shell');
    this.playControls = this.mount.querySelector('[data-play-controls]');
    this.controlsMenu = this.mount.querySelector('[data-controls-menu]');
    this.controlsToggle = this.mount.querySelector('[data-action="controls-toggle"]');
    this.legacy = new LegacyV31Adapter({ mount: host });

    this.mount.addEventListener('click', (event) => {
      const action = event.target.closest('[data-action]')?.dataset.action;

      if (action === 'legacy') {
        this.legacy.launch();
        this.#setPlayMode(true);
      }

      if (action === 'exit-game') {
        this.#setPlayMode(false);
      }

      if (action === 'controls-toggle') {
        const opening = this.controlsMenu.hidden;
        this.controlsMenu.hidden = !opening;
        this.controlsToggle.setAttribute('aria-expanded', String(opening));
      }

      if (action === 'test-lab') {
        this.controlsMenu.hidden = true;
        this.controlsToggle?.setAttribute('aria-expanded', 'false');
        this.testLab?.toggle(true);
      }
    });
  }

  #setPlayMode(active) {
    this.shell.classList.toggle('is-playing', active);
    document.body.classList.toggle('game-active', active);
    this.playControls.hidden = !active;
    this.controlsMenu.hidden = true;
    this.controlsToggle.setAttribute('aria-expanded', 'false');

    if (active) {
      this.legacy.focus();
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }

  #wireEvents() {
    this.bus.on('scenario:start', (scenario) => {
      this.runtimeState.activeScenario = scenario.id;
      this.mount.querySelector('[data-status="scenario"]').textContent = scenario.label;
    });
    this.bus.on('world:set-time', ({ timeOfDay }) => { this.runtimeState.timeOfDay = timeOfDay; });
    this.bus.on('world:set-weather', ({ weather }) => { this.runtimeState.weather = weather; });
    this.bus.on('simulation:step', () => { this.runtimeState.simulationSteps += 1; });
  }

  #frame(timestamp) {
    const delta = this.lastFrame === 0 ? 0 : (timestamp - this.lastFrame) / 1000;
    this.lastFrame = timestamp;
    this.clock.update(delta, (step, time) => this.bus.emit('simulation:step', { step, time }));
    const simulationStatus = this.mount.querySelector('[data-status="simulation"]');
    simulationStatus.textContent = this.clock.paused ? 'Paused' : `${this.clock.timeScale}Ã—`;
    requestAnimationFrame((nextTimestamp) => this.#frame(nextTimestamp));
  }
}